import React from "react";
import { Navigate, Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Users, Tags, FileText, LogOut, Settings, CreditCard, BarChart2, BellRing } from "lucide-react";
import { Button } from "./ui/button";

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  if (user === null) return <div className="h-screen w-screen flex items-center justify-center">Yükleniyor...</div>;
  if (user === false) return <Navigate to="/login" />;

  const isAdmin = user?.role === "admin";
  const isAssistant = user?.role === "assistant";
  const isStaff = isAdmin || isAssistant;

  if (!isStaff && location.pathname === "/") {
    return <Navigate to="/transactions" replace />;
  }

  const links = isStaff ? [
    { to: "/", label: "Özet", icon: <LayoutDashboard size={18} /> },
    { to: "/transactions", label: "İşlemler", icon: <FileText size={18} /> },
    ...(isAdmin ? [{ to: "/assistant-requests", label: "Yardımcı İstekleri", icon: <BellRing size={18} /> }] : []),
    { to: "/report", label: "Rapor", icon: <BarChart2 size={18} /> },
    { to: "/accounts", label: "Cari Hesaplar", icon: <Users size={18} /> },
    { to: "/tags", label: "Etiketler", icon: <Tags size={18} /> },
    { to: "/payment-methods", label: "Ödeme Yöntemleri", icon: <CreditCard size={18} /> },
    ...(isAdmin ? [{ to: "/settings", label: "Kullanıcı Ayarları", icon: <Settings size={18} /> }] : [])
  ] : [
    { to: "/transactions", label: "Hesap Ekstresi", icon: <FileText size={18} /> }
  ];

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background">
      {/* Sidebar */}
      <aside className="w-full md:w-64 bg-white border-r border-border shrink-0 flex flex-col md:sticky md:top-0 md:h-screen z-10">
        <div className="h-16 flex items-center px-6 border-b border-border">
          <h1 className="text-xl font-heading font-bold tracking-tight">Ön Muhasebe</h1>
        </div>
        <nav className="flex-1 py-4 px-3 flex flex-col gap-1 overflow-y-auto">
          {links.map((link) => (
            <Link key={link.to} to={link.to}>
              <div
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  location.pathname === link.to
                    ? "bg-primary text-primary-foreground"
                    : "text-secondary-foreground hover:bg-neutral-100"
                }`}
              >
                {link.icon}
                {link.label}
              </div>
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-border mt-auto">
          <Button
            variant="ghost"
            className="w-full justify-start text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={logout}
            data-testid="logout-button"
          >
            <LogOut size={18} className="mr-2" />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-border flex items-center px-6 md:px-8 shrink-0">
          <h2 className="text-lg font-heading font-medium">Hoşgeldiniz, {user.username} {isAssistant && <span className="ml-2 px-2 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider">Yardımcı Yönetici</span>}</h2>
        </header>
        <div className="p-6 md:p-8 flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
